/**
 * Geometry for the migrated selection chrome, ahead of it taking over from `SelectionChrome`.
 *
 * Produces everything the chrome needs to draw — frame outline, eight resize anchors, the rotation
 * handle's attachment point and direction, and the rotation pivot — in **document space**, leaving
 * the caller to project into screen pixels. Keeping the projection out means this stays pure and
 * testable, and the visual layer can multiply by the current zoom itself.
 *
 * Two deliberate departures from today's chrome:
 *
 * - The frame tracks the **rotated visual box** (`selectionChromeFrameOfShape`), so it hugs the
 *   geometry the user can see instead of standing off an inset shape's edges.
 * - Nothing is measured from the DOM, so the frame is correct on the first frame and at 0° rotation,
 *   where the measured path lost to `canvasAabbOfRotatedPathBounds`'s placement-frame substitution.
 *
 * The geometric rect remains authoritative for every move/resize/rotate computation — this module
 * describes *presentation only*. In particular `pivot` is the geometric centre, which for an inset
 * shape is deliberately **not** the frame's own centre.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type Point,
  type Rect,
  rotatePointAbout,
  rotatedRectCorners,
  selectionChromeFrameOfShape,
  selectionChromeUnionOfShapes,
} from '@/lib/shapes/shape-coordinates'
import { snapLayoutBoxToCanvasZoomGrid } from '@/lib/transforms/canvas-layout-pixel-snap'
import type { ChromeHandleAnchor } from './types/chrome-handle-anchor'
import type { SelectionChromePreview } from './types/selection-chrome-preview'
import type { SelectionChromeFrame } from './types/selection-chrome-frame'

/** Anchor points for the eight resize handles on an unrotated rect, in document space. */
export function chromeHandleAnchors(rect: Rect): ChromeHandleAnchor[] {
  const left = rect.x
  const right = rect.x + rect.width
  const top = rect.y
  const bottom = rect.y + rect.height
  const midX = rect.x + rect.width / 2
  const midY = rect.y + rect.height / 2

  return [
    { key: 'nw', x: left, y: top },
    { key: 'n', x: midX, y: top },
    { key: 'ne', x: right, y: top },
    { key: 'w', x: left, y: midY },
    { key: 'e', x: right, y: midY },
    { key: 'sw', x: left, y: bottom },
    { key: 's', x: midX, y: bottom },
    { key: 'se', x: right, y: bottom },
  ]
}

function previewFromRect(rect: Rect, pivot: Point, rotationDeg: number): SelectionChromePreview {
  const rad = (rotationDeg * Math.PI) / 180

  return {
    rect,
    corners: rotatedRectCorners(rect, pivot, rotationDeg),
    handles: chromeHandleAnchors(rect).map((anchor) => {
      const rotated = rotatePointAbout({ x: anchor.x, y: anchor.y }, pivot, rotationDeg)

      return { key: anchor.key, x: rotated.x, y: rotated.y }
    }),
    rotationAnchor: rotatePointAbout({ x: rect.x + rect.width / 2, y: rect.y }, pivot, rotationDeg),
    // Local "up" is (0,-1); rotating it by the frame angle keeps the stem perpendicular to the
    // top edge at any rotation.
    rotationDirection: { x: Math.sin(rad), y: -Math.cos(rad) },
    pivot,
    rotationDeg,
  }
}

/**
 * The unrotated frame for a selection, in document space.
 *
 * A single object gets an oriented frame following its own rotation. A multi-object selection has no
 * single orientation, so it gets the axis-aligned union of every member's rotated visual box, with
 * the pivot at that union's centre.
 *
 * Returns `null` for an empty selection.
 */
export function selectionChromeFrameForSelection(shapes: ShapeData[]): SelectionChromeFrame | null {
  if (shapes.length === 0) return null

  if (shapes.length === 1) {
    return selectionChromeFrameOfShape(shapes[0])
  }

  const union = selectionChromeUnionOfShapes(shapes)

  if (!union) return null

  return {
    rect: union,
    pivot: { x: union.x + union.width / 2, y: union.y + union.height / 2 },
    rotationDeg: 0,
  }
}

/**
 * Grow a rect about its own centre so each axis is at least `min`. Only a short axis changes.
 *
 * Exported because the text-linking port layer has to apply the identical floor (WMPCM-9199).
 * Ports anchor to the same rect the chrome does (`drawableRectOfShape` via
 * `linkingChromeRectOfFrame`), so when the chrome inflated a sub-`MIN_CHROME_SIZE_PX` frame here
 * and the ports did not, the n/s handles walked away from the in/out ports as the user zoomed out.
 * That layer is inside the canvas transform, so it passes `min / scale` — see `chromeGeometry`'s
 * note on why the round trip is unavoidable there.
 */
export function withMinimumExtent(rect: Rect, min: number): Rect {
  if (!(min > 0)) return rect
  const width = Math.max(rect.width, min)
  const height = Math.max(rect.height, min)

  if (width === rect.width && height === rect.height) return rect

  return {
    x: rect.x + rect.width / 2 - width / 2,
    y: rect.y + rect.height / 2 - height / 2,
    width,
    height,
  }
}

/**
 * Project a frame into screen pixels and produce everything the chrome draws.
 *
 * Both the projection and the minimum-size floor happen here, in that order, which is the point:
 * `minScreenPx` is a *screen* measurement (two 16px handle hit targets must not overlap), so in
 * screen space it is a plain `Math.max`. Inside the canvas transform the same floor has to be
 * expressed as `min / scale` and then multiplied back up by the transform — a division/multiplication
 * round trip that exists only to survive the transform, and that rounds differently at every zoom.
 *
 * Pass `scale: 1, minScreenPx: 0` to stay in document space.
 *
 * `devicePixelSnap: true` snaps `frame.rect` to the same device-pixel grid
 * `ShapeRenderer`'s shell snaps to (`snapLayoutBoxToCanvasZoomGrid`, same `scale` value doubling as
 * `canvasZoom`) before projecting. Without it, the shell's crisp-rendering snap and this frame's exact
 * math independently round each edge, which can leave up to ~1 device px of stroke sitting outside the
 * frame on some edges and a matching gap on others — worse the more zoomed in, since the shape's own
 * stroke renders larger while the frame line stays a literal 1px. Snapping only the *rect* passed into
 * this presentation-only projection, never `selectionChromeFrameOfShape`/`drawableRectOfShape` or any
 * gesture math, which stay exact.
 */
export function chromeGeometry(
  frame: SelectionChromeFrame,
  options?: { scale?: number; minScreenPx?: number; devicePixelSnap?: boolean }
): SelectionChromePreview {
  const scale = options?.scale ?? 1
  const documentRect = options?.devicePixelSnap
    ? snapLayoutBoxToCanvasZoomGrid(frame.rect, scale)
    : frame.rect
  const rect = withMinimumExtent(
    {
      x: documentRect.x * scale,
      y: documentRect.y * scale,
      width: documentRect.width * scale,
      height: documentRect.height * scale,
    },
    options?.minScreenPx ?? 0
  )

  return previewFromRect(
    rect,
    { x: frame.pivot.x * scale, y: frame.pivot.y * scale },
    frame.rotationDeg
  )
}
